import { useCommunityContext } from 'providers';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { ROUTES } from 'shared/constants';
import { filterAllowedRoutesForUser } from 'shared/utils/url';

import { useCurrentUserRoleAddons } from './useCurrentUserRoleAddons';
import { useCurrentUserRoles } from './useCurrentUserRoles';

/**
 * React hook that returns the list of routes the current user is authorized to access within the selected community, based on their role and available addons.
 */
export function useCurrentCommunityAllowedRoutes() {
  const { pathname } = useLocation();
  const { isPlatformAdmin, loading: userRolesLoading } = useCurrentUserRoles();
  const { selectedCommunity, loading: communityLoading } = useCommunityContext();
  const { data: userRoleAddons, loading: userRoleAddonsLoading } = useCurrentUserRoleAddons();

  const loading = communityLoading || userRoleAddonsLoading || userRolesLoading;

  const authorizedRoutes = useMemo(() => {
    const routes = selectedCommunity?.layout?.items || [];

    if (isPlatformAdmin) {
      // Bypass authorization checks for platform admins.
      return routes;
    }

    return filterAllowedRoutesForUser(routes, userRoleAddons);
  }, [isPlatformAdmin, selectedCommunity, userRoleAddons]);

  const isRouteAuthorized = useMemo(() => {
    if (
      pathname.startsWith(ROUTES.user.search.index) ||
      pathname.startsWith(ROUTES.user.settings.index)
    ) {
      // These routes are always authorized
      return true;
    }

    return authorizedRoutes.some(route => matchPath(pathname, { path: route.route as string }));
  }, [authorizedRoutes, pathname]);

  return {
    /**
     * Returns the list of routes that the current user is authorized to access based on their role addons and the selected community's layout.
     */
    authorizedRoutes,

    /**
     * Checks if the current route is authorized for the user based on their role addons.
     * This is useful for conditionally rendering components or redirecting users.
     */
    isRouteAuthorized,

    loading,
  };
}
